import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { queryManager } from 'ember-apollo-client';

import requestStaffMembersColumnPrint from 'my-phorest/gql/mutations/request-staff-members-column-print.graphql';

export default class ColumnsPrinterService extends Service {
  @service electronApp;
  @service printingDevices;
  @service router;
  @service session;
  @service('browser/window') window;

  @queryManager apollo;

  @tracked isBrowserColumnPrintOpen = false;
  @tracked isPrintingColumn = false;
  @tracked data;

  #browserPrintPromiseResolve;

  async print(data, { branchId, forceLocalPrinting = false } = {}) {
    if (this.electronApp.isRunningInElectron) {
      this.isPrintingColumn = true;
      try {
        return await this.printElectron(data, data.staffMemberIds, {
          branchId,
          forceLocalPrinting,
        });
      } finally {
        this.isPrintingColumn = false;
      }
    } else {
      return new Promise((resolve) => {
        this.#browserPrintPromiseResolve = resolve;
        this.data = data;
        this.isPrintingColumn = true;
        this.isBrowserColumnPrintOpen = true;
      });
    }
  }

  @action
  async printElectron(
    data,
    ids,
    { branchId, forceLocalPrinting = false } = {}
  ) {
    await this.electronApp.verifyHasPrinterToPrintTo();

    if (!forceLocalPrinting) {
      const remoteTargetId = await this.electronApp.getRemotePrintingTarget();

      if (remoteTargetId) {
        const result = await this.apollo.mutate(
          {
            mutation: requestStaffMembersColumnPrint,
            variables: {
              date: data.date,
              staffIds: ids,
              printingDeviceId: remoteTargetId,
              withNotes: data.withNotes,
            },
          },
          'requestStaffMembersColumnPrint'
        );

        this.printingDevices.showPrintRequestSentSuccessNotificationTask.perform(
          remoteTargetId
        );
        return result;
      }
    }

    for (let i = 0; i < ids.length; i++) {
      const accountId = this.session.getAccountIdByBranchId(branchId);
      const path = this.router.urlFor(
        'accounts.account.print.columns',
        accountId,
        {
          queryParams: {
            withNotes: data.withNotes,
            date: data.date,
            staffMemberIds: [ids[i]],
          },
        }
      );
      const { protocol, host } = this.window.location;
      const printURL = new URL(`${protocol}//${host}${path}`);

      await new Promise((resolve, reject) => {
        this.electronApp.printReceipt(
          printURL.toString(),
          {
            successCb: resolve,
            failureCb: reject,
          },
          { waitForDataLoadedAttr: true }
        );
      });
    }
  }

  @action
  onWebPrintDone() {
    this.isBrowserColumnPrintOpen = false;
    this.isPrintingColumn = false;
    this.#browserPrintPromiseResolve?.();
  }
}
